import React, { FC } from 'react';
import { View } from 'react-native';

import { scale } from '@components/frame';

import { isWeb } from '@helpers';

import { getBoxShadowStyle } from '@styles/shadow';

import Part from '@components/machines/components/detailsComponents/activeMachine/components/Part';
import Line from '@components/common/line';
import ColorWrapper from '@styles/colors';
import { NOT_PANEL_VIEW_MODE_WIDTH, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import Loading from '@components/loading';
import ImagesContainer, { ImagesContainerMode } from '@v2/pages/common/components/ImagesContainer/ImagesContainer';
import Parts from '@components/search/parts';
import { ItemName } from '@v2/pages/batch/details/modules/batch/components/BatchDetailsBlock/BatchDetailsBlockContent';
import { DOCUMENT_ENTITY_TYPE } from '@components/imageHandling/common';
import { useManagePartGeneralContent } from '@v2/pages/machines/modules/now/components/Part/hooks/useManagePartGeneralContent';
import { getPartFieldValue } from '@v2/pages/machines/modules/now/components/Part/helpers';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import type { WithColor } from '@styles/colors';
import type { PartSearchItem } from '@interfaces/search';

export interface PartGeneralContentProps extends WithColor {
  noMachine?: boolean;
  maxDetailsWidth?: number;
  partDetails?: PartSearchItem[];
  loading?: boolean;
  withImages?: boolean;
  currentPartId?: string | number | null;
  partDataLoadingForList?: boolean;
  bg?: string;
  displaySinglePart?: boolean;
}

const PartGeneralContent: FC<PartGeneralContentProps> = (props) => {
  const {
    C,
    loading,
    maxDetailsWidth = NOT_PANEL_VIEW_MODE_WIDTH,
    withImages,
    bg = 'Background/Light/Field',
  } = props;

  const {
    panelViewMode,
    isEditModeForPartSwitchOn,
    editingPartData,
    selectedItem,
    containerWidth,
    partDetails,
    partId,
    partName,
    shouldShowPartNameAsText,
    shouldDisplaySinglePart,
    shouldDisplayMultiplePart,
    shouldNotShowLoader,
    ncFilesLine,
    onPartNamePress,
    onPartImagePress,
    onContainerLayout,
    setEditingPartData,
    generateImageURL,
    setSelectedNCFile,
  } = useManagePartGeneralContent(props);

  const partData = [
    {
      name: 'Part',
      key: 'partName',
      testID: TEST_IDS.partGeneralContent.field('partName'),
      ...(shouldShowPartNameAsText || !partId
        ? { value: partId ? partName : '' }
        : {
            ValueComponent: (
              <ItemName
                {...{
                  C,
                  text: (isEditModeForPartSwitchOn && editingPartData?.partDetails?.partName) || partName || '',
                  onPress: onPartNamePress,
                }}
              />
            ),
            valueInTextFormat: partName,
          }),
      isText: shouldShowPartNameAsText || !partId,
    },
    {
      name: 'Drawing',
      key: 'drawing',
      testID: TEST_IDS.partGeneralContent.field('drawing'),
      value: getPartFieldValue(partDetails, 'drawing'),
      isText: true,
    },
    {
      name: 'Revision',
      key: 'revision',
      testID: TEST_IDS.partGeneralContent.field('revision'),
      value: getPartFieldValue(partDetails, 'revision'),
      isText: true,
    },
    {
      name: 'Pos',
      key: 'position',
      testID: TEST_IDS.partGeneralContent.field('position'),
      value: getPartFieldValue(partDetails, 'position'),
      isText: true,
    },
    {
      name: 'Description',
      key: 'description',
      testID: TEST_IDS.partGeneralContent.field('description'),
      value: getPartFieldValue(partDetails, 'description'),
      isText: true,
    },
  ];

  const borderRadiusConfig = {
    borderRadius: scale(SCREEN_BORDER_RADIUS),
  };

  return (
    <View
      onLayout={onContainerLayout}
      style={{ flex: 1, width: '100%', alignItems: 'center' }}
    >
      <View
        style={[
          {
            flex: 1,
            flexDirection: panelViewMode ? 'row' : 'column',
            width: '100%',
          },
          { maxWidth: maxDetailsWidth },
        ]}
      >
        <View
          style={[
            {
              width: '100%',
              flex: 1,
            },
          ]}
        >
          {(loading || !shouldNotShowLoader) && (
            <View testID={TEST_IDS.partGeneralContent.loading} style={{ marginTop: scale(40) }}>
              <Loading background="transparent" />
            </View>
          )}

          {!loading && shouldNotShowLoader && (
            <>
              {shouldDisplayMultiplePart && (
                <Parts
                  {...{
                    totalSearchResult: partDetails?.length ?? 0,
                    searchResult: partDetails ?? [],
                    isError: false,
                    loaded: !loading,
                    C,
                    isSmallMode: false,
                    isLineMode: false,
                    fetchData: () => {},
                    generateImageURL,
                    recentlyViewed: [],
                    setRecentlyViewed: () => {},
                    isTableMode: false,
                    withoutHorizontalPaddings: true,
                    withoutMarginTop: true,
                    width: containerWidth,
                    setSelectedNCFile,
                  }}
                />
              )}

              {shouldDisplaySinglePart && (
                <View
                  testID={TEST_IDS.partGeneralContent.block}
                  style={[
                    isWeb && {
                      backgroundColor: C(bg),
                    },
                    !isWeb && {
                      overflow: 'hidden',
                    },
                    borderRadiusConfig,
                    isWeb && getBoxShadowStyle({ type: 'Field', C }),
                  ]}
                >
                  {withImages && (
                    <ImagesContainer
                      {...{
                        maxDetailsWidth,
                        panelViewMode: false,
                        mode: ImagesContainerMode.ROW,
                        documentRetrieveId: partId,
                        documentRetrieveKey: DOCUMENT_ENTITY_TYPE.Part,
                        onImagePress: onPartImagePress,
                      }}
                    />
                  )}
                  <Part
                    {...{
                      ...props,
                      bg,
                      partId,
                      isEditModeForPartSwitchOn,
                      editingPartData,
                      setEditingPartData,
                      noMachine: props.noMachine,
                      selectedItem,
                      panelViewMode,
                      data: partData.filter((v) => v.name === 'Part' || ('value' in v && v.value)),
                    }}
                  />
                </View>
              )}
            </>
          )}

          {!loading && shouldNotShowLoader && partId && shouldDisplaySinglePart && (
            <View
              style={[
                { marginTop: scale(21) },
                isWeb && { borderRadius: scale(SCREEN_BORDER_RADIUS) },
                isWeb && getBoxShadowStyle({ type: 'Field', C }),
              ]}
            >
              {ncFilesLine.map((item, index) => (
                <Line
                  {...{
                    item,
                    index,
                    array: ncFilesLine,
                    C,
                    roundTop: true,
                    roundBottom: true,
                    bg,
                    bottomBorder: false,
                    topBorder: false,
                  }}
                  key={item.name}
                />
              ))}
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

export default ColorWrapper(PartGeneralContent);
